import { useMemo } from 'react';
import {
  Flex,
  Heading,
  Text,
  TextField,
  Button,
  Icon,
  IconButton,
  DropdownMenu,
  RadioGroup,
  Spinner,
  Badge,
} from '@scale/scaleui-radix';
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { AuditRubricCriterion } from '@scale/shared-types';

export const FillCriterionAnswerOptions = ({
  criterion,
  errorCategories,
  isLoadingErrorCategories,
  selectedOption,
  setSelectedOption,
  selectedOptionScore,
}: {
  criterion: AuditRubricCriterion;
  errorCategories: Record<string, number>; //  string to max score mapping
  isLoadingErrorCategories: boolean;
  selectedOption?: string;
  setSelectedOption: (option: string) => void;
  selectedOptionScore?: number;
}) => {
  const appliedErrorCategories = useMemo(
    () =>
      criterion.errorCategories.filter(
        category => selectedOptionScore && errorCategories[category] >= selectedOptionScore,
      ),
    [criterion.errorCategories, selectedOptionScore, errorCategories],
  );
  return (
    <Flex direction="column" gap="2">
      <Text weight="medium">{criterion.questionText}</Text>
      <RadioGroup.Root value={selectedOption} onValueChange={setSelectedOption}>
        {criterion.answerOptions.map((option, index) => (
          <RadioGroup.Item value={option.text} key={index}>
            <Text>{option.text}</Text>
          </RadioGroup.Item>
        ))}
      </RadioGroup.Root>
      {selectedOptionScore !== undefined && (
        <Flex direction="row" gap="2">
          <Text size="1" className="italic">
            Error categories applied:
          </Text>
          {isLoadingErrorCategories ? (
            <Spinner />
          ) : (
            <Text size="1" className="italic">
              {appliedErrorCategories.length > 0 ? appliedErrorCategories.join(', ') : 'None'}
            </Text>
          )}
        </Flex>
      )}
    </Flex>
  );
};

const ViewCriterionAnswerOptions = ({ criterion }: { criterion: AuditRubricCriterion }) => {
  return (
    <Flex direction="column" gap="2">
      <Text color="neutral">Rating Options</Text>
      <div className="flex flex-wrap gap-2">
        {criterion.answerOptions.map((option, index) => (
          <Badge key={index} color="neutral">
            <Text size="1" className="font-medium">
              {option.text} ({option.score})
            </Text>
          </Badge>
        ))}
      </div>
    </Flex>
  );
};

const EditCriterionAnswerOptions = ({
  criterion,
  setCriterion,
  error,
}: {
  criterion: AuditRubricCriterion;
  setCriterion: (criterion: AuditRubricCriterion) => void;
  error?: string;
}) => {
  const setOptionByIndex = (index: number) => (option: { text: string; score: number }) => {
    setCriterion({
      ...criterion,
      answerOptions: criterion.answerOptions.map((o, i) => (i === index ? option : o)),
    });
  };

  const renderOption = (
    option: { text: string; score: number },
    setOption: (option: { text: string; score: number }) => void,
    removeOption: () => void,
    hasError: boolean,
  ) => {
    return (
      <Flex direction="row" gap="2" align="center">
        <TextField.Root
          style={{ flex: 1, ...(hasError ? { boxShadow: 'inset 0 0 0 1px #dc2626' } : {}) }}
          size="2"
          placeholder="Answer choice"
          value={option.text}
          onChange={e => setOption({ ...option, text: e.target.value })}
          color={hasError ? 'red' : undefined}
        />
        <TextField.Root
          style={{ width: 80, ...(hasError ? { boxShadow: 'inset 0 0 0 1px #dc2626' } : {}) }}
          size="2"
          type="number"
          placeholder="Score 1-5"
          value={option.score}
          onChange={e =>
            setOption({ ...option, score: Math.max(1, Math.min(5, Number(e.target.value))) })
          }
          color={hasError ? 'red' : undefined}
        />
        <IconButton variant="ghost" color="neutral" onClick={removeOption} size="1">
          <Icon label="delete" color="neutral" icon={faTrash} size="2" />
        </IconButton>
      </Flex>
    );
  };

  const addOption = () => {
    setCriterion({
      ...criterion,
      answerOptions: [...criterion.answerOptions, { text: '', score: 3 }],
    });
  };

  const removeOptionByIndex = (index: number) => () => {
    setCriterion({
      ...criterion,
      answerOptions: criterion.answerOptions.filter((_, i) => i !== index),
    });
  };

  const loadDisagreeAgreeTemplate = () => {
    setCriterion({
      ...criterion,
      answerOptions: [
        { text: 'Strongly Disagree', score: 1 },
        { text: 'Disagree', score: 2 },
        { text: 'Neutral', score: 3 },
        { text: 'Agree', score: 4 },
        { text: 'Strongly Agree', score: 5 },
      ],
    });
  };
  const loadYesNoTemplate = () => {
    setCriterion({
      ...criterion,
      answerOptions: [
        { text: 'No', score: 1 },
        { text: 'Yes', score: 5 },
      ],
    });
  };

  return (
    <Flex direction="column" gap="2">
      <Flex direction="row" gap="2" justify="between" align="center">
        <Heading size="2">Rating Options</Heading>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger>
            <Button variant="outline" size="1" color="neutral">
              Load Template
              <DropdownMenu.TriggerIcon />
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content size="2">
            <DropdownMenu.Item onClick={loadDisagreeAgreeTemplate}>
              <Flex direction="column" gap="1">
                <Text weight="medium" size="1">
                  Disagree-Agree
                </Text>
                <Text size="1" color="neutral">
                  5 options
                </Text>
              </Flex>
            </DropdownMenu.Item>
            <DropdownMenu.Separator />
            <DropdownMenu.Item onClick={loadYesNoTemplate}>
              <Flex direction="column" gap="1">
                <Text weight="medium" size="1">
                  No-Yes
                </Text>
                <Text size="1" color="neutral">
                  2 options
                </Text>
              </Flex>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </Flex>
      <Flex direction="row" gap="2" align="center">
        <Heading size="1" style={{ flex: 1 }}>
          Option Name
        </Heading>
        <Heading size="1" style={{ width: 80 }}>
          Score
        </Heading>
        <div style={{ width: 24 }} />
      </Flex>
      {criterion.answerOptions.map((option, index) =>
        renderOption(option, setOptionByIndex(index), removeOptionByIndex(index), !!error),
      )}
      <Flex gap="2" justify="start">
        <Button variant="ghost" color="neutral" onClick={addOption} size="1">
          <Icon label="add" color="neutral" icon={faPlus} size="2" />
          Add Option
        </Button>
      </Flex>
      {error && (
        <Text size="2" color="red">
          {error}
        </Text>
      )}
    </Flex>
  );
};

export const CriterionAnswerOptions = ({
  criterion,
  setCriterion,
  isEditingCriterion,
  error,
}: {
  criterion: AuditRubricCriterion;
  setCriterion: (criterion: AuditRubricCriterion) => void;
  isEditingCriterion: boolean;
  error?: string;
}) => {
  return isEditingCriterion ? (
    <EditCriterionAnswerOptions criterion={criterion} setCriterion={setCriterion} error={error} />
  ) : (
    <ViewCriterionAnswerOptions criterion={criterion} />
  );
};
